import { useState, useContext } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  Tooltip,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/PowerSettingsNew';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import UserIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InsightsMenuIcon from '@mui/icons-material/Insights';
import { ConfigurationsMenuIcon } from '@/resources/icons/menu_bar_icons/ConfigurationsMenuIcon';
import { DevicesMenuIcon } from '@/resources/icons/menu_bar_icons/DevicesMenuIcon';
import { OrganizationMenuIcon } from '@/resources/icons/menu_bar_icons/OrganizationMenuIcon';
import { UsersMenuIcon } from '@/resources/icons/menu_bar_icons/UsersMenuIcon';
import {
  useUserContext,
  UserContext,
} from '@/context_providers/user_context/UserProvider';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import { isAdmin, isRoleAccepted } from '../routing/UserRoutes/AcceptedRoles';
import { useHandleScreenSize } from '@/context_providers/screen_context/ScreenSizeProvider';
import COLORS from '@/resources/style/Themes';
import SwitchViewBanner from '../banners/dev_banners/SwitchViewBanner';
import NavbarLogo from './logo/NavbarLogo';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { Routes } from '@/enums/Routes';
import { ArrowDropDown, Help } from '@mui/icons-material';
import { NotificationBubble } from '@/components/notifications/NotificationBubble';
import { NewBadge } from '@/components/notifications/NewBadge';

export const UserNavBar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const { isAuthenticated, logout } = useAuth0();
  const { userExists } = useContext(UserContext);
  const { orgUser, userRole } = useUserUpdaterProvider();
  const { joinRequests } = useOrganizationUpdaterProvider();
  const { screenType } = useHandleScreenSize();
  const { isSafeguardAdmin, isSafeguardManufacturingUser, isSafeguardDev } =
    useUserContext();
  const { t } = useTranslation();
  const location = useLocation();
  const joinRequestCount = joinRequests.length;

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logoutWithRedirect = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const isRouteActive = (route) =>
    route === Routes.HOME
      ? location.pathname === Routes.HOME
      : location.pathname.startsWith(route);

  const getNavLabelStyles = (route) => ({
    display: {
      xs: 'inline',
      md: isRouteActive(route) ? 'inline' : 'none',
      lg: 'inline',
    },
    fontSize: 15,
  });

  const getNavButtonStyles = (route) => {
    const isActive = isRouteActive(route);

    return {
      color: COLORS.WHITE,
      borderRadius: 3,
      textTransform: 'none',
      display: 'inline-flex',
      padding: '10px 14px',
      position: 'relative',

      '& .MuiButton-startIcon': {
        mr: { xs: 1, md: isActive ? 1 : 0, lg: 1 },
        ml: { xs: -0.5, md: isActive ? -0.5 : 0, lg: -0.5 },
      },

      '&::after': {
        content: '""',
        position: 'absolute',
        left: 14,
        right: 14,
        bottom: 4,
        height: '2px',
        backgroundColor: COLORS.WHITE,
        transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.3s ease',
      },

      '&:hover::after': {
        transform: 'scaleX(1)',
      },

      '&:hover': {
        color: COLORS.WHITE,
      },
    };
  };

  return (
    <>
      {orgUser && userExists && isAuthenticated ? (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            height: '65px',
            zIndex: 1300,
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              {/* Logo for Desktop */}
              <Box
                sx={{ display: { xs: 'none', md: 'flex' }, maxWidth: '140px' }}
              >
                <NavbarLogo screenType={screenType} />
              </Box>

              {/* Mobile Menu Button */}
              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label="open drawer"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{ display: { xs: 'block', md: 'none' } }}
                >
                  {isRoleAccepted(userRole) && [
                    <MenuItem
                      sx={{ textTransform: 'none' }}
                      key="emergencyMap"
                      component={RouterNavLink}
                      to={Routes.HOME}
                      onClick={handleCloseNavMenu}
                    >
                      <LocationOnIcon sx={{ mr: 1, fontSize: '1.75rem' }} />
                      <Typography textAlign="center">
                        {t(StringKeys.MAP)}
                      </Typography>
                    </MenuItem>,
                    isAdmin(userRole) && [
                      <MenuItem
                        sx={{ textTransform: 'none' }}
                        key="userManagement"
                        component={RouterNavLink}
                        to={Routes.USER_MANAGEMENT_ALL_USERS}
                        onClick={handleCloseNavMenu}
                      >
                        <UsersMenuIcon sx={{ fontSize: '1.5rem', mr: 1 }} />
                        <Typography textAlign="center">
                          {t(StringKeys.USERS)}
                        </Typography>
                      </MenuItem>,
                      <MenuItem
                        sx={{ textTransform: 'none' }}
                        key="deviceManagement"
                        component={RouterNavLink}
                        to={Routes.DEVICE_MANAGEMENT}
                        onClick={handleCloseNavMenu}
                      >
                        <DevicesMenuIcon sx={{ fontSize: '1.6rem', mr: 1 }} />
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <Typography textAlign="center">
                            {t(StringKeys.DEVICES)}
                          </Typography>
                          <Box sx={{ position: 'absolute', top: -8, right: -20 }}>
                            <NewBadge />
                          </Box>
                        </Box>
                      </MenuItem>,
                      <MenuItem
                        sx={{ textTransform: 'none' }}
                        key="emergencyResponseManagement"
                        component={RouterNavLink}
                        to={Routes.EMERGENCY_RESPONDER_MANAGEMENT}
                        onClick={handleCloseNavMenu}
                      >
                        <HeadsetMicIcon sx={{ mr: 1, fontSize: '1.75rem' }} />
                        <Typography textAlign="center">
                          {t(StringKeys.EMERGENCY_RESPONDERS)}
                        </Typography>
                      </MenuItem>,
                      <MenuItem
                        sx={{ textTransform: 'none' }}
                        key="compassConfigurations"
                        component={RouterNavLink}
                        to={Routes.COMPASS_CONFIGURATIONS}
                        onClick={handleCloseNavMenu}
                      >
                        <ConfigurationsMenuIcon sx={{ fontSize: '1.75rem', mr: 1 }} />
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <Typography textAlign="center">
                            {t(StringKeys.CONFIGURATIONS)}
                          </Typography>
                          <Box sx={{ position: 'absolute', top: -8, right: -20 }}>
                            <NewBadge />
                          </Box>
                        </Box>
                      </MenuItem>,
                      <MenuItem
                        sx={{ textTransform: 'none' }}
                        key="organizationManagement"
                        component={RouterNavLink}
                        to={Routes.ORGANIZATION_MANAGEMENT}
                        onClick={handleCloseNavMenu}
                      >
                        <OrganizationMenuIcon sx={{ fontSize: '1.5rem', mr: 1 }} />
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'flex-start',
                            pr: joinRequestCount > 0 ? 1.5 : 0,
                          }}
                        >
                          <Typography textAlign="center">
                            {t(StringKeys.ORGANIZATION)}
                          </Typography>
                          {joinRequestCount > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -6,
                                right: -10,
                              }}
                            >
                              <NotificationBubble count={joinRequestCount} />
                            </Box>
                          )}
                        </Box>
                      </MenuItem>,
                      <MenuItem
                        sx={{ textTransform: 'none' }}
                        key="insights"
                        component={RouterNavLink}
                        to={Routes.INSIGHTS}
                        onClick={handleCloseNavMenu}
                      >
                        <InsightsMenuIcon sx={{ fontSize: '1.5rem', mr: 1 }} />
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <Typography textAlign="center">
                            {t(StringKeys.INSIGHTS)}
                          </Typography>
                          <Box sx={{ position: 'absolute', top: -8, right: -20 }}>
                            <NewBadge />
                          </Box>
                        </Box>
                      </MenuItem>,
                    ],
                  ]}
                </Menu>
              </Box>

              {/* Logo for Mobile */}
              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                <NavbarLogo screenType={screenType} />
              </Box>

              {/* Desktop Menu Items */}
              <Box
                sx={{
                  flexGrow: 1,
                  display: { xs: 'none', md: 'flex' },
                  pl: 5,
                  gap: {md: 1, lg: 2},
                  alignItems: 'center',
                }}
              >
                {isRoleAccepted(userRole) && (
                  <>
                    <Button
                      component={RouterNavLink}
                      to={Routes.HOME}
                      sx={getNavButtonStyles(Routes.HOME)}
                      startIcon={
                        <LocationOnIcon
                          sx={{ color: COLORS.WHITE, fontSize: '1.4rem' }}
                        />
                      }
                    >
                      <Box
                        component="span"
                        sx={getNavLabelStyles(Routes.HOME)}
                      >
                        {t(StringKeys.MAP)}
                      </Box>
                    </Button>
                    {isAdmin(userRole) && (
                      <>
                        <Button
                          component={RouterNavLink}
                          to={Routes.USER_MANAGEMENT_ALL_USERS}
                          sx={getNavButtonStyles(Routes.USER_MANAGEMENT)}
                          startIcon={
                            <UsersMenuIcon sx={{ fontSize: '1.2rem' }} />
                          }
                        >
                          <Box
                            component="span"
                            sx={getNavLabelStyles(Routes.USER_MANAGEMENT)}
                          >
                            {t(StringKeys.USERS)}
                          </Box>
                        </Button>
                        <Button
                          component={RouterNavLink}
                          to={Routes.DEVICE_MANAGEMENT}
                          sx={[
                            getNavButtonStyles(Routes.DEVICE_MANAGEMENT),
                            {
                              '& .MuiButton-startIcon > *:nth-of-type(1)': {
                                fontSize: '1.1rem',
                              },
                            },
                          ]}
                          startIcon={<DevicesMenuIcon />}
                        >
                          <Box
                            component="span"
                            sx={getNavLabelStyles(Routes.DEVICE_MANAGEMENT)}
                          >
                            {t(StringKeys.DEVICES)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 4,
                            }}
                          >
                            <NewBadge />
                          </Box>
                        </Button>
                        <Button
                          component={RouterNavLink}
                          to={Routes.EMERGENCY_RESPONDER_MANAGEMENT}
                          sx={getNavButtonStyles(
                            Routes.EMERGENCY_RESPONDER_MANAGEMENT,
                          )}
                          startIcon={
                            <HeadsetMicIcon
                              sx={{ color: COLORS.WHITE, fontSize: '1.4rem' }}
                            />
                          }
                        >
                          <Box
                            component="span"
                            sx={getNavLabelStyles(
                              Routes.EMERGENCY_RESPONDER_MANAGEMENT,
                            )}
                          >
                            {t(StringKeys.RESPONDERS)}
                          </Box>
                        </Button>
                        <Button
                          component={RouterNavLink}
                          to={Routes.COMPASS_CONFIGURATIONS}
                          sx={[
                            getNavButtonStyles(Routes.COMPASS_CONFIGURATIONS),
                            {
                              '& .MuiButton-startIcon > *:nth-of-type(1)': {
                                fontSize: '1.35rem',
                              },
                            },
                          ]}
                          startIcon={<ConfigurationsMenuIcon />}
                        >
                          <Box
                            component="span"
                            sx={getNavLabelStyles(Routes.COMPASS_CONFIGURATIONS)}
                          >
                            {t(StringKeys.CONFIGURATIONS)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 4,
                            }}
                          >
                            <NewBadge />
                          </Box>
                        </Button>
                        <Button
                          component={RouterNavLink}
                          to={Routes.ORGANIZATION_MANAGEMENT}
                          sx={getNavButtonStyles(
                            Routes.ORGANIZATION_MANAGEMENT,
                          )}
                          startIcon={
                            <OrganizationMenuIcon sx={{ fontSize: '1.2rem' }} />
                          }
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              pr:
                                joinRequestCount > 0
                                  ? {
                                      xs: 2,
                                      md: isRouteActive(
                                        Routes.ORGANIZATION_MANAGEMENT,
                                      )
                                        ? 2
                                        : 1,
                                      lg: 2,
                                    }
                                  : 0,
                              minWidth: joinRequestCount > 0 ? '8px' : 'auto',
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                font: 'inherit',
                                lineHeight: 'inherit',
                                ...getNavLabelStyles(
                                  Routes.ORGANIZATION_MANAGEMENT,
                                ),
                              }}
                            >
                              {t(StringKeys.ORGANIZATION)}
                            </Typography>
                            {joinRequestCount > 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -6,
                                  right: -10,
                                }}
                              >
                                <NotificationBubble count={joinRequestCount} />
                              </Box>
                            )}
                          </Box>
                        </Button>
                        <Button
                          component={RouterNavLink}
                          to={Routes.INSIGHTS}
                          sx={getNavButtonStyles(Routes.INSIGHTS)}
                          startIcon={
                            <InsightsMenuIcon sx={{ fontSize: '1.2rem' }} />
                          }
                        >
                          <Box
                            component="span"
                            sx={getNavLabelStyles(Routes.INSIGHTS)}
                          >
                            {t(StringKeys.INSIGHTS)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 4,
                            }}
                          >
                            <NewBadge />
                          </Box>
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Box>

              {/* User Menu */}
              <Box
                sx={{
                  flexGrow: 0,
                  display: 'flex',
                  cursor: 'pointer',
                  alignItems: 'center',
                }}
              >
                <Box
                  onClick={handleOpenUserMenu}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ color: COLORS.WHITE, mr: 1 }}>
                    {orgUser.first_name} {orgUser.last_name}
                  </Typography>
                  <Tooltip title="Open settings">
                    <IconButton sx={{ p: 0 }}>
                      <ArrowDropDown sx={{ color: COLORS.WHITE }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {isRoleAccepted(userRole) && [
                    // Use array instead of fragments
                    <MenuItem
                      key="profile"
                      component={RouterNavLink}
                      to={Routes.PROFILE}
                      onClick={handleCloseUserMenu}
                    >
                      <UserIcon sx={{ mr: 1 }} />
                      {t(StringKeys.PROFILE)}
                    </MenuItem>,

                    <MenuItem
                      key="emergencyHistory"
                      component={RouterNavLink}
                      to={Routes.EMERGENCY_HISTORY}
                      onClick={handleCloseUserMenu}
                    >
                      <MedicalServicesIcon sx={{ mr: 1 }} />
                      {t(StringKeys.EMERGENCY_HISTORY)}
                    </MenuItem>,
                  ]}
                  <MenuItem
                    key="help"
                    component={RouterNavLink}
                    to={Routes.HELP}
                    onClick={handleCloseUserMenu}
                  >
                    <Help sx={{ mr: 1 }} />
                    {t(StringKeys.HELP)}
                  </MenuItem>
                  <MenuItem key="logout" onClick={logoutWithRedirect}>
                    <LogoutIcon sx={{ mr: 1 }} />
                    {t(StringKeys.LOG_OUT)}
                  </MenuItem>
                </Menu>
                {(isSafeguardAdmin ||
                  isSafeguardManufacturingUser ||
                  isSafeguardDev) && <SwitchViewBanner />}
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      ) : (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            height: '65px',
            zIndex: 1300,
          }}
        >
          <Toolbar></Toolbar>
        </AppBar>
      )}
    </>
  );
};

export default UserNavBar;
